/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { ServiceType, ServiceItem, CustomerInfo } from "../utils/tickets";
import { useServiceSelection } from "./useServiceSelection";

export interface ChangeRequestFormState {
  // Non-KE fields
  selectedTopicLabel: string;
  selectedTwoLevelLabel: string;
  
  // Common fields
  title: string;
  priority: string;
  description: string;
  
  // Contact fields
  sendResponse: boolean;
  contactName: string;
  contactEmail: string;
  selectedCountryCode: string;
  contactPhoneNumber: string;
  
  // KE-specific fields
  selectedCustomerId: number | null;
  selectedCustomerNameDisplay: string;
  selectedServiceIds: number[];
  selectedServiceTypes: ServiceType[];
  selectedSubscriptionIds: string[];
  selectedServiceLabels: string[];
  
  // Attachments
  attachmentFiles: FileList | null;
}

export interface UseChangeRequestFormReturn {
  // Form state
  formState: ChangeRequestFormState;
  
  // Setters for common fields
  setTitle: (value: string) => void;
  setPriority: (value: string) => void;
  setDescription: (value: string) => void;
  
  // Setters for contact fields
  setSendResponse: (value: boolean) => void;
  setContactName: (value: string) => void;
  setContactEmail: (value: string) => void;
  setSelectedCountryCode: (value: string) => void;
  setContactPhoneNumber: (value: string) => void;
  
  // Setters for KE-specific fields
  setSelectedCustomerId: (value: number | null) => void;
  setSelectedCustomerNameDisplay: (value: string) => void;
  setSelectedServiceIds: (value: number[]) => void;
  setSelectedServiceTypes: (value: ServiceType[]) => void;
  setSelectedSubscriptionIds: (value: string[]) => void;
  setSelectedServiceLabels: (value: string[]) => void;
  
  // Handlers
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomerSelect: (customerNameDisplay: string, uniqueCustomers: CustomerInfo[]) => void;
  handleServiceSelect: (selectedLabels: string[], filteredServiceItems: ServiceItem[]) => void;
  
  // Reset function
  resetForm: () => void;
}

/**
 * Custom hook to manage Change Request form state
 */
export const useChangeRequestForm = (): UseChangeRequestFormReturn => {
  // Non-KE fields (managed by useTopicsLoader, but included for completeness)
  const [selectedTopicLabel, setSelectedTopicLabel] = useState("");
  const [selectedTwoLevelLabel, setSelectedTwoLevelLabel] = useState("");
  
  // Common fields
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("");
  const [description, setDescription] = useState("");
  
  // Contact fields
  const [sendResponse, setSendResponse] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
  const [contactPhoneNumber, setContactPhoneNumber] = useState("");
  
  // KE-specific fields — shared with the other Kontrol Edge ticket modals
  const serviceSelection = useServiceSelection();
  const { reset: resetServiceSelection } = serviceSelection;
  const {
    selectedCustomerId,
    selectedCustomerNameDisplay,
    selectedServiceIds,
    selectedServiceTypes,
    selectedSubscriptionIds,
    selectedServiceLabels,
  } = serviceSelection;

  // Attachments
  const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);

  // File change handler
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachmentFiles(e.target.files);
    }
  }, []);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setSelectedTopicLabel("");
    setSelectedTwoLevelLabel("");
    setTitle("");
    setPriority("");
    setDescription("");
    setSendResponse(false);
    setContactName("");
    setContactEmail("");
    setSelectedCountryCode("+1");
    setContactPhoneNumber("");
    setAttachmentFiles(null);
    resetServiceSelection();
  }, [resetServiceSelection]);

  const formState: ChangeRequestFormState = {
    selectedTopicLabel,
    selectedTwoLevelLabel,
    title,
    priority,
    description,
    sendResponse,
    contactName,
    contactEmail,
    selectedCountryCode,
    contactPhoneNumber,
    selectedCustomerId,
    selectedCustomerNameDisplay,
    selectedServiceIds,
    selectedServiceTypes,
    selectedSubscriptionIds,
    selectedServiceLabels,
    attachmentFiles,
  };

  return {
    formState,
    setTitle,
    setPriority,
    setDescription,
    setSendResponse,
    setContactName,
    setContactEmail,
    setSelectedCountryCode,
    setContactPhoneNumber,
    setSelectedCustomerId: serviceSelection.setSelectedCustomerId,
    setSelectedCustomerNameDisplay:
      serviceSelection.setSelectedCustomerNameDisplay,
    setSelectedServiceIds: serviceSelection.setSelectedServiceIds,
    setSelectedServiceTypes: serviceSelection.setSelectedServiceTypes,
    setSelectedSubscriptionIds: serviceSelection.setSelectedSubscriptionIds,
    setSelectedServiceLabels: serviceSelection.setSelectedServiceLabels,
    handleFileChange,
    handleCustomerSelect: serviceSelection.handleCustomerSelect,
    handleServiceSelect: serviceSelection.handleServiceSelect,
    resetForm,
  };
};

